import React, { useState, useEffect } from 'react';
import './Shop.css';

function Shop({ onClose }) {
  const [items, setItems] = useState([]);
  const [userEquipment, setUserEquipment] = useState([]);
  const [profile, setProfile] = useState(null);
  const [notification, setNotification] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [equipRes, userEquipRes, profileRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/equipment`, { headers }),
        fetch(`${process.env.REACT_APP_API_URL}/equipment/user`, { headers }),
        fetch(`${process.env.REACT_APP_API_URL}/profile`, { headers })
      ]);

      const itemsData = await equipRes.json();
      const userEquipData = await userEquipRes.json();
      const profileData = await profileRes.json();
      
      console.log('User Equipment:', userEquipData); // DEBUG
      
      setItems(Array.isArray(itemsData) ? itemsData : []);
      setUserEquipment(Array.isArray(userEquipData) ? userEquipData : []);
      setProfile(profileData);
    } catch (error) {
      console.error('Error:', error);
      showNotification('❌ Error al cargar datos', 'error');
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const isOwned = (item) => {
    if (!Array.isArray(userEquipment)) return false;
    // Los items de recuperación nunca se marcan como "owned" porque son consumibles
    if (item.tipo === 'recovery') return false;
    return userEquipment.some(eq => 
      eq.tipo === item.tipo && 
      eq.nivel === item.nivel && 
      eq.nombre === item.nombre
    );
  };
  
  const isEquipped = (item) => {
    if (!Array.isArray(userEquipment)) return false;
    return userEquipment.some(eq => 
      eq.tipo === item.tipo && 
      eq.nivel === item.nivel && 
      eq.nombre === item.nombre && 
      eq.equipped
    );
  };

  const buyItem = async (id, price, name, tipo) => {
    if (!profile || profile.monedas < price) {
      showNotification('❌ Monedas insuficientes', 'error');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/equipment/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ equipmentId: id })
      });
      
      if (res.ok) {
        const data = await res.json();
        
        // Si es un item de recuperación, mostrar energía restaurada
        if (tipo === 'recovery' && data.energiaRecuperada) {
          showNotification(`✅ ${name} usado! +${data.energiaRecuperada} energía`, 'success');
        } else {
          showNotification(`✅ ${name} comprado!`, 'success');
        }
        
        await loadData();
        window.dispatchEvent(new Event('profileUpdate'));
      } else {
        const error = await res.json();
        showNotification(`❌ ${error.error || 'Error en la compra'}`, 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      showNotification('❌ Error en la compra', 'error');
    }
  };

  const equipItem = async (id, name) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/equipment/equip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ equipmentId: id })
      });
      
      if (res.ok) {
        showNotification(`✅ ${name} equipado!`, 'success');
        // Esperar un poco antes de recargar
        setTimeout(async () => {
          await loadData();
          window.dispatchEvent(new Event('profileUpdate'));
        }, 300);
      } else {
        const error = await res.json();
        showNotification(`❌ ${error.error || 'Error al equipar'}`, 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      showNotification('❌ Error al equipar', 'error');
    }
  };

  const unequipItem = async (id, name) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/equipment/unequip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ equipmentId: id })
      });
      
      if (res.ok) {
        showNotification(`✅ ${name} desequipado`, 'success');
        // Esperar un poco antes de recargar
        setTimeout(async () => {
          await loadData();
          window.dispatchEvent(new Event('profileUpdate'));
        }, 300);
      } else {
        const error = await res.json();
        showNotification(`❌ ${error.error || 'Error al desequipar'}`, 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      showNotification('❌ Error al desequipar', 'error');
    }
  };

  const getImage = (item) => {
    const t = item.tipo;
    const nivel = item.nivel; // usar el campo nivel de la BD
    
    // Mapear nivel a sufijo de imagen
    const levelSuffix = {
      'starter': '',
      'basic': '-basic',
      'pro': '-pro',
      'elite': '-elite',
      'rose': '-rose',
      'military': '-military',
      'black': '-black',
      'raptors': '-raptors'
    };
    
    const suffix = levelSuffix[nivel] || '';
    
    if (t === 'gloves') return `/assets/equipment/gloves/gloves${suffix}.png`;
    if (t === 'feet') return `/assets/equipment/feet/footpads${suffix}.png`;
    if (t === 'head') return `/assets/equipment/head/headgear${suffix}.png`;
    if (t === 'suit') return `/assets/equipment/suit/uniform${suffix}.png`;
    if (t === 'recovery') {
      const n = item.nombre.toLowerCase();
      if (n.includes('suplemento')) return '/assets/equipment/recovery/suplemento-basico.png';
      if (n.includes('kit')) return '/assets/equipment/recovery/kitmedico.png';
      if (n.includes('barra')) return '/assets/equipment/recovery/barraenergy.png';
      return '/assets/equipment/recovery/energy.png';
    }
    return '/assets/equipment/gloves/gloves.png';
  };

  const groupByType = (items) => {
    return items.reduce((acc, item) => {
      if (item.tipo !== 'skill') {
        if (!acc[item.tipo]) acc[item.tipo] = [];
        acc[item.tipo].push(item);
      }
      return acc;
    }, {});
  };

  const getFilteredItems = () => {
    if (filter === 'all') return items;
    return items.filter(item => item.tipo === filter);
  };

  const typeNames = { 
    gloves: 'Guantes', 
    feet: 'Protectores', 
    head: 'Cabezales', 
    suit: 'Trajes', 
    recovery: 'Recuperación' 
  };

  const filteredItems = getFilteredItems();
  const grouped = groupByType(filteredItems);

  return (
    <div className="overlay" onClick={onClose}>
      {notification && (
        <div className={`shop-notification ${notification.type}`}>
          {notification.message}
        </div>
      )}
      
      <div className="shop-container" onClick={(e) => e.stopPropagation()}>
        <div className="shop-header">
          <h2>TIENDA DE EQUIPAMIENTO</h2>
          <div className="user-money">💰 {profile?.monedas || 0}</div>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="shop-filters">
          <button 
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            Todos
          </button>
          <button 
            className={`filter-btn ${filter === 'gloves' ? 'active' : ''}`}
            onClick={() => setFilter('gloves')}
          >
            Guantes
          </button>
          <button 
            className={`filter-btn ${filter === 'feet' ? 'active' : ''}`}
            onClick={() => setFilter('feet')}
          >
            Pads de Pie
          </button>
          <button 
            className={`filter-btn ${filter === 'head' ? 'active' : ''}`}
            onClick={() => setFilter('head')}
          >
            Cabezal
          </button>
          <button 
            className={`filter-btn ${filter === 'suit' ? 'active' : ''}`}
            onClick={() => setFilter('suit')}
          >
            Trajes
          </button>
          <button 
            className={`filter-btn ${filter === 'recovery' ? 'active' : ''}`}
            onClick={() => setFilter('recovery')}
          >
            Recuperación
          </button>
        </div>

        <div className="shop-content">
          {Object.entries(grouped).map(([type, typeItems]) => (
            <div key={type} className="equipment-category">
              <div className="items-grid">
                {typeItems.map(item => {
                    const owned = isOwned(item);
                    const equipped = isEquipped(item);
                    const isRecovery = item.tipo === 'recovery';
                    const status = equipped ? 'equipped' : owned ? 'owned' : 'available';
                    const badge = isRecovery ? 'Consumible' : (equipped ? 'Equipado' : owned ? 'Comprado' : 'Disponible');
                    const canBuy = !owned && profile && profile.monedas >= item.price;

                    return (
                      <div key={item.id} className={`shop-item ${equipped ? 'equipped' : ''}`}>
                        <div className={`item-badge badge-${isRecovery ? 'available' : status}`}>{badge}</div>
                        <img 
                          src={getImage(item)} 
                          alt={item.nombre} 
                          className="item-icon"
                          onError={(e) => e.target.src = '/assets/equipment/gloves/gloves.png'}
                        />
                        <h3 className="item-name">{item.nombre}</h3>
                        
                        <div className="item-bonuses">
                          {item.power_bonus > 0 && isRecovery && <span>🔋 +{Math.round(item.power_bonus * 100)} energía</span>}
                          {item.power_bonus > 0 && !isRecovery && <span>💪 +{Math.round(item.power_bonus * 100)}%</span>}
                          {item.defense_bonus > 0 && <span>🛡️ +{Math.round(item.defense_bonus * 100)}%</span>}
                          {item.speed_bonus > 0 && <span>⚡ +{Math.round(item.speed_bonus * 100)}%</span>}
                          {item.technique_bonus > 0 && <span>🎯 +{Math.round(item.technique_bonus * 100)}%</span>}
                        </div>

                        {/* Items de recuperación siempre se pueden comprar */}
                        {isRecovery && (
                          <>
                            <div className="item-price">💰 {item.price}</div>
                            <button 
                              className={`buy-btn ${(!profile || profile.monedas < item.price || profile.energia >= 100) ? 'disabled' : ''}`}
                              onClick={() => profile && profile.monedas >= item.price && profile.energia < 100 && buyItem(item.id, item.price, item.nombre, item.tipo)}
                              disabled={!profile || profile.monedas < item.price || profile.energia >= 100}
                            >
                              {profile && profile.energia >= 100 ? 'ENERGÍA LLENA' : (profile && profile.monedas >= item.price ? 'USAR' : 'SIN MONEDAS')}
                            </button>
                          </>
                        )}
                        
                        {/* Equipamiento normal */}
                        {!isRecovery && !owned && (
                          <>
                            <div className="item-price">💰 {item.price}</div>
                            <button 
                              className={`buy-btn ${!canBuy ? 'disabled' : ''}`}
                              onClick={() => canBuy && buyItem(item.id, item.price, item.nombre, item.tipo)}
                              disabled={!canBuy}
                            >
                              {canBuy ? 'COMPRAR' : 'SIN MONEDAS'}
                            </button>
                          </>
                        )}
                        {!isRecovery && owned && !equipped && (
                          <button 
                            className="equip-btn" 
                            onClick={() => equipItem(item.id, item.nombre)}
                          >
                            EQUIPAR
                          </button>
                        )}
                        {!isRecovery && equipped && (
                          <button 
                            className="unequip-btn" 
                            onClick={() => unequipItem(item.id, item.nombre)}
                          >
                            DESEQUIPAR
                          </button>
                        )}
                      </div>
                    );
                })}}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Shop;
